import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, vModelDynamic as _vModelDynamic, normalizeClass as _normalizeClass, withDirectives as _withDirectives, vModelText as _vModelText, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, vModelSelect as _vModelSelect, withModifiers as _withModifiers, withKeys as _withKeys, vModelCheckbox as _vModelCheckbox, renderSlot as _renderSlot } from "vue"

const _hoisted_1 = ["for"]
const _hoisted_2 = {
  key: 0,
  class: "text-danger"
}
const _hoisted_3 = ["id", "type", "placeholder", "onUpdate:modelValue", "required", "readonly"]
const _hoisted_4 = ["id", "placeholder", "onUpdate:modelValue", "required", "readonly", "rows"]
const _hoisted_5 = ["id", "onUpdate:modelValue"]
const _hoisted_6 = { key: 1 }
const _hoisted_7 = { key: 0 }
const _hoisted_8 = { key: 0 }
const _hoisted_9 = ["src"]
const _hoisted_10 = {
  key: 1,
  class: "text-muted"
}
const _hoisted_11 = { key: 1 }
const _hoisted_12 = {
  key: 0,
  class: "mb-2"
}
const _hoisted_13 = ["src"]
const _hoisted_14 = ["id", "onChange", "accept", "required"]
const _hoisted_15 = {
  key: 1,
  class: "mt-2"
}
const _hoisted_16 = { class: "text-muted" }
const _hoisted_17 = ["id", "onUpdate:modelValue", "required", "disabled"]
const _hoisted_18 = ["value"]
const _hoisted_19 = { key: 6 }
const _hoisted_20 = {
  key: 0,
  class: "input-group mb-2"
}
const _hoisted_21 = ["placeholder", "onKeydown"]
const _hoisted_22 = ["onClick"]
const _hoisted_23 = { class: "d-flex flex-wrap gap-1" }
const _hoisted_24 = ["onClick"]
const _hoisted_25 = {
  key: 7,
  class: "form-check"
}
const _hoisted_26 = ["onUpdate:modelValue", "id", "disabled"]
const _hoisted_27 = ["for"]
const _hoisted_28 = {
  key: 0,
  class: "text-danger"
}
const _hoisted_29 = {
  key: 8,
  class: "form-check form-switch mt-2"
}
const _hoisted_30 = ["onUpdate:modelValue", "id", "disabled"]
const _hoisted_31 = ["for"]
const _hoisted_32 = {
  key: 0,
  class: "text-danger"
}
const _hoisted_33 = {
  key: 0,
  class: "multiselect-wrapper"
}
const _hoisted_34 = ["onClick"]
const _hoisted_35 = {
  key: 0,
  class: "text-muted"
}
const _hoisted_36 = {
  key: 1,
  class: "multiselect-selected-text"
}
const _hoisted_37 = {
  key: 0,
  class: "multiselect-dropdown"
}
const _hoisted_38 = ["onClick"]
const _hoisted_39 = ["checked", "onChange"]
const _hoisted_40 = {
  key: 0,
  class: "multiselect-option text-muted"
}
const _hoisted_41 = {
  key: 1,
  class: "d-flex flex-wrap gap-1 mt-2"
}
const _hoisted_42 = ["onClick"]
const _hoisted_43 = { key: 1 }
const _hoisted_44 = { class: "d-flex flex-wrap gap-1" }

import { ref, watch } from "vue";

// --- Define the structure for form fields ---
export interface FormField {
  key: string;
  label: string;
  type: "text" | "email" | "number" | "textarea" | "color" | "file" | "select" | "checkbox" | "chip" | "date" | "multiselect" | "switch";
  placeholder?: string;
  required?: boolean;
  readonly?: boolean;
  rows?: number;
  accept?: string;
  options?: Array<{ _id: string; title: string }>;
}


import { defineComponent } from "vue";
const __default__ = defineComponent({
  name: "BaseForm",
});

export default /*@__PURE__*/_defineComponent({
  ...__default__,
  props: /*@__PURE__*/_mergeModels({
    fields: {}
  }, {
    "modelValue": { required: true },
    "modelModifiers": {},
  }),
  emits: /*@__PURE__*/_mergeModels(["submit"], ["update:modelValue"]),
  setup(__props: any, { expose: __expose, emit: __emit }) {

const chipInput = ref("");

const addChip = (key: string) => {
  if (chipInput.value.trim()) {
    if (!localFormData.value[key]) localFormData.value[key] = [];
    localFormData.value[key].push(chipInput.value.trim());
    chipInput.value = "";
  }
};

const removeChip = (key: string, index: number) => {
  localFormData.value[key].splice(index, 1);
};

// --- Multiselect helpers ---
const openMultiselect = ref<string | null>(null);

const toggleMultiselect = (key: string) => {
  openMultiselect.value = openMultiselect.value === key ? null : key;
};

const toggleMultiselectOption = (key: string, optionId: string) => {
  if (!localFormData.value[key]) localFormData.value[key] = [];
  const idx = localFormData.value[key].indexOf(optionId);
  if (idx === -1) {
    localFormData.value[key] = [...localFormData.value[key], optionId];
  } else {
    localFormData.value[key] = localFormData.value[key].filter((id: string) => id !== optionId);
  }
};

const removeMultiselectOption = (key: string, optionId: string) => {
  localFormData.value[key] = localFormData.value[key].filter((id: string) => id !== optionId);
};
// --- Define component props ---
const props = __props;

// --- Define v-model (requires Vue 3.4+) ---
// This creates an implicit modelValue prop and 'update:modelValue' event
const model = _useModel<Record<string, any>>(__props, "modelValue");

// --- Define component emits ---
// Corrected syntax: specify parameter name 'data' and its type
const emit = __emit;

// --- Component State ---
// Local copy for v-model binding within the form.
// Initialized with the value passed via v-model from the parent.
const localFormData = ref<Record<string, any>>({ ...model.value });

// --- Loop Prevention Flags ---
// These flags help prevent infinite update loops between localFormData and model.value.
let isUpdatingFromModel = false; // Tracks updates coming from the parent via v-model
let isUpdatingFromLocal = false; // Tracks updates coming from user input/local changes

// --- Watchers ---

/**
 * Watch for changes from the PARENT component (via v-model).
 * This updates the LOCAL state (localFormData) when the parent's data changes.
 */
watch(
  model,
  (newVal) => {
    // Prevent loop: If we just emitted an update locally, ignore the parent's echo
    if (isUpdatingFromLocal) {
      isUpdatingFromLocal = false; // Reset flag
      // console.log("BaseForm: Ignoring model update as it originated locally");
      return;
    }

    // Set flag to indicate this update is from the model (parent)
    isUpdatingFromModel = true;
    // console.log("BaseForm: Model changed from parent, updating localFormData:", newVal);

    // Update local copy. Using spread operator for a simple shallow copy.
    localFormData.value = { ...newVal };
  },
  { deep: true }
); // Deep watch is necessary for nested objects

/**
 * Watch for changes in the LOCAL form data (user input).
 * This emits 'update:modelValue' to the PARENT component when the form changes.
 */
watch(
  localFormData,
  (newVal) => {
    // Prevent loop: If we just received an update from the model, don't emit back
    if (isUpdatingFromModel) {
      isUpdatingFromModel = false; // Reset flag
      // console.log("BaseForm: Ignoring local update as it originated from model");
      return;
    }

    // Set flag to indicate this update is from local changes
    isUpdatingFromLocal = true;
    // console.log("BaseForm: localFormData changed, emitting update to parent:", newVal);

    // Emit the updated data back to the parent component via v-model
    // Emitting a copy ({ ...newVal }) is safer to prevent direct mutations by parent
    emit("update:modelValue", { ...newVal });
  },
  { deep: true }
); // Deep watch is necessary for nested objects

// --- Handlers ---

/**
 * Handles file input changes.
 * Updates localFormData and triggers the watcher to emit the change.
 */
const handleFileChange = (event: Event, key: string) => {
  const target = event.target as HTMLInputElement;
  if (target.files && target.files[0]) {
    localFormData.value[key] = target.files[0];
  } else {
    localFormData.value[key] = null;
  }
  // Modifying localFormData.value[key] will automatically trigger the
  // watch(localFormData, ...) watcher above, which will then emit the update.
};

/**
 * Handles form submission.
 * Performs basic validation and emits the 'submit' event with the current form data.
 */
const onSubmit = () => {
  // Basic validation check based on field definitions
  const isValid = props.fields.every((field) => {
    if (field.required) {
      const value = localFormData.value[field.key];
      if (field.type === "file") {
        // For required file inputs, check if a File object is present
        return value instanceof File;
      }
      // For other required fields, check if value exists and is not just whitespace
      return (
        value !== undefined && value !== null && value.toString().trim() !== ""
      );
    }
    // Non-required fields are considered valid by default
    return true;
  });

  if (isValid) {
    // If valid, emit the 'submit' event with the current form data
    emit("submit", { ...localFormData.value });
  } else {
    // If invalid, log a warning (parent component can handle UI feedback)
    console.warn("BaseForm: Form validation failed.");
  }
};

// --- Expose internal state (optional) ---
// Expose localFormData if the parent component needs direct access
__expose({ formData: localFormData });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("form", {
    onSubmit: _withModifiers(onSubmit, ["prevent"])
  }, [
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.fields, (field) => {
      return (_openBlock(), _createElementBlock("div", {
        key: field.key,
        class: "mb-3"
      }, [
        (field.type !== 'checkbox')
          ? (_openBlock(), _createElementBlock("label", {
              key: 0,
              for: `baseform-${field.key}`,
              class: "form-label"
            }, [
              _createTextVNode(_toDisplayString(field.label) + " ", 1),
              (field.required)
                ? (_openBlock(), _createElementBlock("span", _hoisted_2, "*"))
                : _createCommentVNode("", true)
            ], 8, _hoisted_1))
          : _createCommentVNode("", true),
        (
          field.type === 'text' ||
          field.type === 'email' ||
          field.type === 'number' ||
          field.type === 'date'
        )
          ? _withDirectives((_openBlock(), _createElementBlock("input", {
              key: 1,
              id: `baseform-${field.key}`,
              type: field.type,
              class: _normalizeClass(["form-control", { 'form-control-plaintext': field.readonly }]),
              placeholder: field.placeholder,
              "onUpdate:modelValue": ($event: any) => ((localFormData.value[field.key]) = $event),
              required: field.required,
              readonly: field.readonly
            }, null, 10, _hoisted_3)), [
              [_vModelDynamic, localFormData.value[field.key]]
            ])
          : (field.type === 'textarea')
            ? _withDirectives((_openBlock(), _createElementBlock("textarea", {
                key: 2,
                id: `baseform-${field.key}`,
                class: _normalizeClass(["form-control", { 'form-control-plaintext': field.readonly }]),
                placeholder: field.placeholder,
                "onUpdate:modelValue": ($event: any) => ((localFormData.value[field.key]) = $event),
                required: field.required,
                readonly: field.readonly,
                rows: field.rows || 3
              }, null, 10, _hoisted_4)), [
                [_vModelText, localFormData.value[field.key]]
              ])
            : (field.type === 'color')
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                  (!field.readonly)
                    ? _withDirectives((_openBlock(), _createElementBlock("input", {
                        key: 0,
                        id: `baseform-${field.key}`,
                        type: "color",
                        class: "form-control form-control-color",
                        "onUpdate:modelValue": ($event: any) => ((localFormData.value[field.key]) = $event)
                      }, null, 8, _hoisted_5)), [
                        [_vModelText, localFormData.value[field.key]]
                      ])
                    : (_openBlock(), _createElementBlock("div", _hoisted_6, [
                        _createElementVNode("span", {
                          class: "badge",
                          style: _normalizeStyle({ backgroundColor: localFormData.value[field.key] })
                        }, _toDisplayString(localFormData.value[field.key]), 5)
                      ]))
                ], 64))
              : (field.type === 'file')
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 4 }, [
                    (field.readonly && localFormData.value[field.key])
                      ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                          (typeof localFormData.value[field.key] === 'string' && (localFormData.value[field.key].match(/\.(jpg|jpeg|png|gif|webp|svg)/i) || localFormData.value[field.key].startsWith('http')))
                            ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                                _createElementVNode("img", {
                                  src: localFormData.value[field.key],
                                  alt: "Preview",
                                  class: "img-thumbnail mb-2",
                                  style: {"max-height":"150px","display":"block"}
                                }, null, 8, _hoisted_9)
                              ]))
                            : (_openBlock(), _createElementBlock("span", _hoisted_10, " File: " + _toDisplayString(localFormData.value[field.key] && (localFormData.value[field.key] as File).name
                ? (localFormData.value[field.key] as File).name
                : localFormData.value[field.key]), 1))
                        ]))
                      : (_openBlock(), _createElementBlock("div", _hoisted_11, [
                          (typeof localFormData.value[field.key] === 'string' && (localFormData.value[field.key].match(/\.(jpg|jpeg|png|gif|webp|svg)/i) || localFormData.value[field.key].startsWith('http')))
                            ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                                _createElementVNode("img", {
                                  src: localFormData.value[field.key],
                                  alt: "Current",
                                  class: "img-thumbnail",
                                  style: {"max-height":"100px"}
                                }, null, 8, _hoisted_13),
                                _cache[2] || (_cache[2] = _createElementVNode("div", { class: "small text-muted" }, "Current image", -1))
                              ]))
                            : _createCommentVNode("", true),
                          _createElementVNode("input", {
                            id: `baseform-${field.key}`,
                            type: "file",
                            class: "form-control",
                            onChange: (e) => handleFileChange(e, field.key),
                            accept: field.accept || '*',
                            required: field.required && !localFormData.value[field.key]
                          }, null, 40, _hoisted_14),
                          (localFormData.value[field.key] && (localFormData.value[field.key] as File).name)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                                _createElementVNode("span", _hoisted_16, "Selected: " + _toDisplayString((localFormData.value[field.key] as File).name), 1)
                              ]))
                            : _createCommentVNode("", true)
                        ]))
                  ], 64))
                : (field.type === 'select')
                  ? _withDirectives((_openBlock(), _createElementBlock("select", {
                      key: 5,
                      id: `baseform-${field.key}`,
                      class: "form-select",
                      "onUpdate:modelValue": ($event: any) => ((localFormData.value[field.key]) = $event),
                      required: field.required,
                      disabled: field.readonly
                    }, [
                      _cache[3] || (_cache[3] = _createElementVNode("option", {
                        disabled: "",
                        value: ""
                      }, "-- Select --", -1)),
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(field.options, (option) => {
                        return (_openBlock(), _createElementBlock("option", {
                          key: option._id,
                          value: option._id
                        }, _toDisplayString(option.title), 9, _hoisted_18))
                      }), 128))
                    ], 8, _hoisted_17)), [
                      [_vModelSelect, localFormData.value[field.key]]
                    ])
                  : (field.type === 'chip')
                    ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                        (!field.readonly)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                              _withDirectives(_createElementVNode("input", {
                                type: "text",
                                class: "form-control",
                                placeholder: field.placeholder,
                                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((chipInput).value = $event)),
                                onKeydown: _withKeys(_withModifiers(($event: any) => (addChip(field.key)), ["prevent"]), ["enter"])
                              }, null, 40, _hoisted_21), [
                                [_vModelText, chipInput.value]
                              ]),
                              _createElementVNode("button", {
                                class: "btn btn-outline-secondary",
                                type: "button",
                                onClick: ($event: any) => (addChip(field.key))
                              }, " Add ", 8, _hoisted_22)
                            ]))
                          : _createCommentVNode("", true),
                        _createElementVNode("div", _hoisted_23, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(localFormData.value[field.key], (chip, index) => {
                            return (_openBlock(), _createElementBlock("span", {
                              key: index,
                              class: "badge bg-primary d-flex align-items-center"
                            }, [
                              _createTextVNode(_toDisplayString(chip) + " ", 1),
                              (!field.readonly)
                                ? (_openBlock(), _createElementBlock("i", {
                                    key: 0,
                                    class: "ms-2 cursor-pointer",
                                    onClick: ($event: any) => (removeChip(field.key, index))
                                  }, "×", 8, _hoisted_24))
                                : _createCommentVNode("", true)
                            ]))
                          }), 128))
                        ])
                      ]))
                    : (field.type === 'checkbox')
                      ? (_openBlock(), _createElementBlock("div", _hoisted_25, [
                          _withDirectives(_createElementVNode("input", {
                            type: "checkbox",
                            "onUpdate:modelValue": ($event: any) => ((model.value[field.key]) = $event),
                            class: "form-check-input",
                            id: `baseform-${field.key}`,
                            disabled: field.readonly
                          }, null, 8, _hoisted_26), [
                            [_vModelCheckbox, model.value[field.key]]
                          ]),
                          _createElementVNode("label", {
                            class: "form-check-label",
                            for: `baseform-${field.key}`
                          }, [
                            _createTextVNode(_toDisplayString(field.label) + " ", 1),
                            (field.required)
                              ? (_openBlock(), _createElementBlock("span", _hoisted_28, "*"))
                              : _createCommentVNode("", true)
                          ], 8, _hoisted_27)
                        ]))
                      : (field.type === 'switch')
                        ? (_openBlock(), _createElementBlock("div", _hoisted_29, [
                            _withDirectives(_createElementVNode("input", {
                              type: "checkbox",
                              "onUpdate:modelValue": ($event: any) => ((model.value[field.key]) = $event),
                              class: "form-check-input",
                              role: "switch",
                              id: `baseform-${field.key}`,
                              disabled: field.readonly
                            }, null, 8, _hoisted_30), [
                              [_vModelCheckbox, model.value[field.key]]
                            ]),
                            _createElementVNode("label", {
                              class: "form-check-label",
                              for: `baseform-${field.key}`
                            }, [
                              _createTextVNode(_toDisplayString(field.label) + " ", 1),
                              (field.required)
                                ? (_openBlock(), _createElementBlock("span", _hoisted_32, "*"))
                                : _createCommentVNode("", true)
                            ], 8, _hoisted_31)
                          ]))
                        : (field.type === 'multiselect')
                          ? (_openBlock(), _createElementBlock(_Fragment, { key: 9 }, [
                              (!field.readonly)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_33, [
                                    _createElementVNode("div", {
                                      class: "multiselect-trigger form-control d-flex align-items-center justify-content-between",
                                      onClick: ($event: any) => (toggleMultiselect(field.key))
                                    }, [
                                      (!localFormData.value[field.key] || localFormData.value[field.key].length === 0)
                                        ? (_openBlock(), _createElementBlock("span", _hoisted_35, _toDisplayString(field.placeholder || '-- Select --'), 1))
                                        : (_openBlock(), _createElementBlock("span", _hoisted_36, _toDisplayString(localFormData.value[field.key].length) + " selected ", 1)),
                                      _cache[4] || (_cache[4] = _createElementVNode("i", {
                                        class: "fas fa-chevron-down ms-2",
                                        style: {"font-size":"0.7rem","color":"#6c757d"}
                                      }, null, -1))
                                    ], 8, _hoisted_34),
                                    (openMultiselect.value === field.key)
                                      ? (_openBlock(), _createElementBlock("div", _hoisted_37, [
                                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(field.options, (option) => {
                                            return (_openBlock(), _createElementBlock("div", {
                                              key: option._id,
                                              class: "multiselect-option",
                                              onClick: _withModifiers(($event: any) => (toggleMultiselectOption(field.key, option._id)), ["stop"])
                                            }, [
                                              _createElementVNode("input", {
                                                type: "checkbox",
                                                class: "form-check-input me-2",
                                                checked: (localFormData.value[field.key] || []).includes(option._id),
                                                onClick: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["stop"])),
                                                onChange: ($event: any) => (toggleMultiselectOption(field.key, option._id))
                                              }, null, 40, _hoisted_39),
                                              _createElementVNode("span", null, _toDisplayString(option.title), 1)
                                            ], 8, _hoisted_38))
                                          }), 128)),
                                          (!field.options || field.options.length === 0)
                                            ? (_openBlock(), _createElementBlock("div", _hoisted_40, " No options available "))
                                            : _createCommentVNode("", true)
                                        ]))
                                      : _createCommentVNode("", true),
                                    (localFormData.value[field.key] && localFormData.value[field.key].length)
                                      ? (_openBlock(), _createElementBlock("div", _hoisted_41, [
                                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(localFormData.value[field.key], (selectedId) => {
                                            return (_openBlock(), _createElementBlock("span", {
                                              key: selectedId,
                                              class: "badge bg-primary d-flex align-items-center"
                                            }, [
                                              _createTextVNode(_toDisplayString((field.options || []).find((o) => o._id === selectedId)?.title || selectedId) + " ", 1),
                                              _createElementVNode("i", {
                                                class: "ms-2 cursor-pointer",
                                                onClick: ($event: any) => (removeMultiselectOption(field.key, selectedId))
                                              }, "×", 8, _hoisted_42)
                                            ]))
                                          }), 128))
                                        ]))
                                      : _createCommentVNode("", true)
                                  ]))
                                : (_openBlock(), _createElementBlock("div", _hoisted_43, [
                                    _createElementVNode("div", _hoisted_44, [
                                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList((localFormData.value[field.key] || []), (selectedId) => {
                                        return (_openBlock(), _createElementBlock("span", {
                                          key: selectedId,
                                          class: "badge bg-secondary"
                                        }, _toDisplayString((field.options || []).find((o) => o._id === selectedId)?.title || selectedId), 1))
                                      }), 128))
                                    ])
                                  ]))
                            ], 64))
                          : _createCommentVNode("", true)
      ]))
    }), 128)),
    _renderSlot(_ctx.$slots, "additional-fields")
  ], 32))
}
}

})